import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/site/Layout";
import { listArticlesPublic, type PublicArticle } from "@/lib/content.functions";
import { Calendar, Clock, ArrowRight, Loader2 } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import diarioHero from "@/assets/diario-hero.jpg";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog & News — PULSO33" },
      {
        name: "description",
        content:
          "Technical articles, product updates and news from the PULSO33 engineering team.",
      },
      { property: "og:title", content: "Blog & News — PULSO33" },
      {
        property: "og:description",
        content:
          "Technical articles, product updates and news from the PULSO33 engineering team.",
      },
      { property: "og:url", content: "https://pulso33.com/blog" },
    ],
    links: [
      { rel: "canonical", href: "https://pulso33.com/blog" },
    ],
  }),
  component: DiarioPage,
});

type Filter = "todos" | "blog" | "noticias";

function formatDate(iso: string | null, locale: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function DiarioPage() {
  const { tk, lang } = useT();
  const hero = useMedia("diario.hero", diarioHero, "Diario");
  const TABS: { id: Filter; label: string }[] = [
    { id: "todos", label: tk("diario.list.a4114a", "All") },
    { id: "blog", label: tk("diario.list.0b9d2b", "Blog") },
    { id: "noticias", label: tk("diario.list.07d1d6", "News") },
  ];
  const [filter, setFilter] = useState<Filter>("todos");
  const list = useServerFn(listArticlesPublic);
  const { data, isLoading } = useQuery({
    queryKey: ["articles-public", lang],
    queryFn: () => list({ data: { locale: lang } }),
  });

  const articles: PublicArticle[] = data?.articles ?? [];
  const filtered = filter === "todos" ? articles : articles.filter((a) => a.category === filter);
  const locale = lang === "pt" ? "pt-PT" : lang === "en" ? "en-GB" : "es-ES";


  return (
    <Layout>
      <section className="relative bg-cover bg-center" style={{ backgroundImage: `url(${hero.url})` }}>
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-10">
          <span className="text-xs tracking-[0.35em] uppercase text-gold">{tk("diario.list.800602", "Journal")}</span>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-tight text-white">{tk("diario.list.1a6ffe", "Blog & News")}</h1>
          <p className="mt-5 max-w-2xl text-white/80">
            {tk("diario.list.1462df", "Engineering notes, product updates and news from the PULSO33 team.")}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex gap-2 border-b border-gold/20 mt-12 mb-10">
          {TABS.map((tab) => {
            const active = filter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-5 py-3 text-xs tracking-[0.25em] uppercase transition-colors border-b-2 -mb-px ${
                  active
                    ? "text-gold border-gold"
                    : "text-muted-foreground border-transparent hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {isLoading ? (
          <div className="flex justify-center py-20 text-muted-foreground">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((a) => (
              <Link
                key={a.slug}
                to="/blog/$slug"
                params={{ slug: a.slug }}
                className="group rounded-2xl border border-gold/15 bg-card/40 p-7 flex flex-col hover:border-gold/50 transition-colors"
              >
                <span
                  className={`self-start text-[10px] tracking-[0.3em] uppercase px-3 py-1 rounded-full border ${
                    a.category === "noticias"
                      ? "border-gold/60 text-gold"
                      : "border-foreground/30 text-foreground/70"
                  }`}
                >
                  {a.category === "noticias" ? tk("diario.list.f8f25f", "News") : tk("diario.list.0b9d2b", "Blog")}
                </span>
                <h2 className="font-serif text-2xl mt-5 leading-snug group-hover:text-gold transition-colors">
                  {a.title}
                </h2>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a.excerpt}</p>
                <div className="mt-6 pt-5 border-t border-gold/10 flex items-center justify-between text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
                  <span className="flex items-center gap-2">
                    <Calendar size={12} className="text-gold" />
                    {formatDate(a.published_at, locale)}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock size={12} className="text-gold" />
                    {a.reading_time}
                  </span>
                </div>
                <span className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-gold">
                  {tk("diario.list.eaf7d9", "Read")} <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <p className="text-center text-muted-foreground py-20">
            {tk("diario.list.4ce713", "No articles in this category yet.")}
          </p>
        )}
      </section>

    </Layout>
  );
}
